import {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { Statused } from 'types/ui'

import { useInputClassname } from '@elements/input-container'
import {
  getDateInputPlaceholderByVenueDateDisplayFormat,
  getDayFrom,
  getDaysInTheMonth,
  getMonthFrom,
  getYearFrom,
  resolveVenueDateDisplayFormat,
} from '@utils/date-time'

export interface DateInputProps extends Statused {
  value: string | number | undefined
  initialValue?: string | number | undefined
  onChange: (val?: string) => void
  disabled?: boolean
  dateDisplayFormat?: string | null
  placeholder?: string
}

const fixDigits = (value: string) =>
  value.length < 2 ? `0${value}` : value.slice(value.length - 2)

export function DateInput({
  value,
  initialValue,
  onChange,
  error,
  warning,
  success,
  disabled = false,
  dateDisplayFormat,
  placeholder,
}: DateInputProps) {
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(
    null
  )
  const [month, setMonth] = useState<string>(
    value ? fixDigits(getMonthFrom(value).toString()) : ''
  )
  const [day, setDay] = useState<string>(
    value ? fixDigits(getDayFrom(value).toString()) : ''
  )
  const [year, setYear] = useState<string>(
    value ? getYearFrom(value).toString() : ''
  )

  const handleMonthChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newMonth = e.target.value
      if (!newMonth) {
        if (!year && !day) onChange(undefined)
        setMonth('')
        return
      }

      const monthNumber = Number(newMonth)
      if (monthNumber < 0 || monthNumber > 13) return

      const monthDigits = newMonth.split('')
      const isTwoDigits = monthDigits.length === 2
      if (isTwoDigits || monthNumber >= 2) {
        setMonth(fixDigits(newMonth))
        const dayElement = containerElement?.querySelector<HTMLElement>(
          'input[data-fieldtype="day"]'
        )
        dayElement?.focus()
      } else {
        setMonth(newMonth)
      }

      if (newMonth && day && year) {
        const dt = new Date(
          Number(year),
          Number(newMonth) - 1,
          Number(day),
          0,
          0,
          0
        )
        onChange(dt.toISOString())
      }
    },
    [containerElement, day, onChange, year]
  )

  const handleDayChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newDay = e.target.value
      if (!newDay) {
        if (!month && !year) onChange(undefined)
        setDay('')
        return
      }

      const maxDay = month
        ? getDaysInTheMonth(
            year ? Number(year) : new Date().getFullYear(),
            Number(month)
          )
        : 31
      const maxDayDigits = maxDay.toString()
      const dayNumber = Number(newDay)
      if (dayNumber < 0 || dayNumber > maxDay) return

      const dayDigits = newDay.split('')
      const isTwoDigits = dayDigits.length >= 2
      const isMaxSingleDigit =
        dayDigits[0] && !dayDigits[1] && Number(dayDigits[0] > maxDayDigits[0])

      if (isTwoDigits || isMaxSingleDigit) {
        setDay(fixDigits(newDay))
        const yearElement = containerElement?.querySelector<HTMLElement>(
          'input[data-fieldtype="year"]'
        )
        yearElement?.focus()
      } else {
        setDay(newDay)
      }

      if (newDay && month && year) {
        const dt = new Date(
          Number(year),
          Number(month) - 1,
          Number(newDay),
          0,
          0,
          0
        )
        onChange(dt.toISOString())
      }
    },
    [containerElement, month, onChange, year]
  )

  const handleYearChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newYear = e.target.value
      if (!newYear) {
        if (!month && !day) onChange(undefined)
        setYear('')
        return
      }

      if (newYear.length > 4) return
      setYear(newYear)

      if (newYear && day && month) {
        const dt = new Date(
          Number(newYear),
          Number(month) - 1,
          Number(day),
          0,
          0,
          0
        )
        onChange(dt.toISOString())
      }
    },
    [day, month, onChange]
  )

  const highlightOnFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => e.target?.select(),
    []
  )

  const className = useInputClassname({
    className:
      'flex items-center justify-start px-3 space-x-1.5 h-[32px] lg:h-[38px] !border !border-standard-light disabled:!border-fg',
    success,
    warning,
    error,
    hasValue: !!value,
  })

  const resolvedDateDisplayFormat = resolveVenueDateDisplayFormat(dateDisplayFormat)
  const defaultPlaceholder =
    getDateInputPlaceholderByVenueDateDisplayFormat(resolvedDateDisplayFormat)
  const [firstPlaceholder, secondPlaceholder] = (placeholder ?? defaultPlaceholder)
    .split('/')
    .map((part) => part.toLowerCase())

  useEffect(() => {
    setMonth(
      initialValue ? fixDigits(getMonthFrom(initialValue).toString()) : ''
    )
    setDay(initialValue ? fixDigits(getDayFrom(initialValue).toString()) : '')
    setYear(initialValue ? getYearFrom(initialValue).toString() : '')
  }, [initialValue])

  return (
    <div className={className} ref={setContainerElement}>
      <input
        className='inline w-[24px] !p-0 text-center !bg-transparent !border-none outline-none'
        data-fieldtype='month'
        inputMode='numeric'
        placeholder={
          resolvedDateDisplayFormat === 'DD/MM/YYYY'
            ? secondPlaceholder || 'mm'
            : firstPlaceholder || 'mm'
        }
        pattern='\d*'
        type='number'
        value={month}
        onChange={handleMonthChange}
        onFocus={highlightOnFocus}
        disabled={disabled}
      />
      <span>/</span>
      <input
        className='inline w-[24px] !p-0 text-center !bg-transparent !border-none outline-none'
        data-fieldtype='day'
        inputMode='numeric'
        placeholder={
          resolvedDateDisplayFormat === 'DD/MM/YYYY'
            ? firstPlaceholder || 'dd'
            : secondPlaceholder || 'dd'
        }
        pattern='\d*'
        type='number'
        value={day}
        onChange={handleDayChange}
        onFocus={highlightOnFocus}
        disabled={disabled}
      />
      <span>/</span>
      <input
        className='inline w-[40px] !p-0 text-center !bg-transparent !border-none outline-none'
        data-fieldtype='year'
        inputMode='numeric'
        placeholder='yyyy'
        pattern='\d*'
        type='number'
        value={year}
        onChange={handleYearChange}
        onFocus={highlightOnFocus}
        disabled={disabled}
      />
      <button
        className='w-full h-full bg-transparent border-none'
        type='button'
        aria-label='Focus month input'
        onClick={() =>
          containerElement
            ?.querySelector<HTMLElement>('input[data-fieldtype="month"]')
            ?.focus()
        }
      ></button>
    </div>
  )
}
